import { getClientTheme } from '@/service/api'

const STORAGE_KEY = 'app-theme'
const defaultLogo = new URL('../assets/images/AmarelLogo.png', import.meta.url)
  .href

const DEFAULT_THEME = {
  primary_color: '#ed3795',
  secondary_color: '#9f68e1',
  logo: defaultLogo,
  favicon: null,
  subdomain: 'amarel'
}

export async function applyTheme() {
  const saved = loadThemeFromStorage()
  const subdomain = getSubdomain()

  if (!saved || (subdomain && saved.subdomain !== subdomain)) {
    try {
      const { data, status } = await getClientTheme(subdomain)

      if (status === 200 && data?.data) {
        const themeData = normalizeTheme(
          {
            primary_color: data.data.primary_color,
            secondary_color: data.data.secondary_color,
            logo: data.data.logo,
            favicon: data.data.favicon
          },
          subdomain
        )

        setTheme(themeData)
        saveThemeToStorage(themeData)
      } else {
        // API responded but not success
        console.error('Theme API returned non-200:', status)
        applyFallbackTheme(saved)
      }
    } catch (error) {
      // Network error / server down / timeout
      console.error('Failed to fetch theme:', error)
      applyFallbackTheme(saved)
    }
  } else {
    setTheme(saved)
  }
}

function applyFallbackTheme(saved) {
  if (saved) {
    setTheme(saved) // Use last known good theme
  } else {
    setTheme(DEFAULT_THEME) // Use hardcoded default
  }
}

function setTheme(theme) {
  const normalized = normalizeTheme(theme)
  const root = document.documentElement
  root.style.setProperty('--primary', normalized.primary_color)
  root.style.setProperty('--secondary', normalized.secondary_color)
}

function loadThemeFromStorage() {
  const saved = localStorage.getItem(STORAGE_KEY)
  return saved ? JSON.parse(saved) : null
}

function saveThemeToStorage(theme) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme))
  } catch (error) {
    console.warn('Failed to persist theme settings:', error)
  }
}

export function getTheme() {
  const saved = loadThemeFromStorage()
  return saved ? normalizeTheme(saved) : { ...DEFAULT_THEME }
}

export function getSubdomain() {
  const hostname = window.location.hostname

  // Ignore localhost or IP addresses
  if (hostname === 'localhost') {
    return 'amarel'
  }

  const parts = hostname.split('.')
  return parts.length > 2 ? parts.slice(0, -2).join('.') : null
}

function normalizeTheme(theme, subdomain = null) {
  if (!theme) return { ...DEFAULT_THEME }

  return {
    primary_color: sanitizeColor(
      theme.primary_color,
      DEFAULT_THEME.primary_color
    ),
    secondary_color: sanitizeColor(
      theme.secondary_color,
      DEFAULT_THEME.secondary_color
    ),
    logo: normalizeLogo(theme.logo),
    favicon: theme.favicon || DEFAULT_THEME.favicon,
    subdomain: subdomain ?? theme.subdomain ?? DEFAULT_THEME.subdomain
  }
}

function sanitizeColor(colorValue, fallback) {
  if (typeof colorValue !== 'string') return fallback
  const trimmed = colorValue.trim()
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
    return trimmed.toLowerCase()
  }
  return fallback
}

function normalizeLogo(rawLogo) {
  if (!rawLogo || typeof rawLogo !== 'string') return DEFAULT_THEME.logo

  const trimmed = rawLogo.trim()
  if (trimmed.startsWith('data:image')) return trimmed
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('//')) {
    return `${window.location.protocol}${trimmed}`
  }

  const base = window.location.origin
  const cleaned = trimmed.replace(/^\/+/, '')
  return `${base}/${cleaned}`
}
